import { repairImageKitUrl } from '@dupe/common/constants'
import type { GiftGuide } from '@dupe/common/notion'
import { kebabCase } from 'lodash'
import Image from 'next/image'
import Link from 'next/link'
import {
  fmtPrice,
  guideCount,
  guideMinPrice,
  productPrice,
} from '~/src/lib/giftGuideDisplay'
import { cn } from '~/src/lib/utils'

// ---------------------------------------------------------------------------
// Derivations from the real Notion obsessions (gift-guide) data
// ---------------------------------------------------------------------------
// Count / price / "from" helpers are shared with the home picks cards — see
// `~/src/lib/giftGuideDisplay`. Only the href shape is local to this surface.

export const guideHref = (g: GiftGuide, ref: string): string => {
  const slug = kebabCase(g.name).replace(/[^\w-]+/g, '')
  return `/obsessions/${slug}/${g.id}?ref=${encodeURIComponent(ref)}`
}

// ---------------------------------------------------------------------------
// Shared atoms
// ---------------------------------------------------------------------------

/** A stack of overlapping round product thumbnails + a "+N · from $X" caption. */
function ProductSwatches({
  guide,
  size = 'md',
}: {
  guide: GiftGuide
  size?: 'sm' | 'md'
}) {
  const shown = (guide.products || []).slice(0, 3)
  const remaining = Math.max(0, guideCount(guide) - shown.length)
  const minPrice = fmtPrice(guideMinPrice(guide))
  const dim = size === 'sm' ? 'size-9' : 'size-[42px]'
  const px = size === 'sm' ? 36 : 42
  if (!shown.length) return null
  return (
    <div className='flex items-center gap-2.5'>
      <div className='flex items-center'>
        {shown.map((p, i) => (
          <span
            key={p.id || p.image || `swatch-${i}`}
            className={cn(
              dim,
              'relative overflow-hidden rounded-full bg-surface ring-2 ring-background',
              i > 0 && '-ml-2.5'
            )}
          >
            {p.image && (
              <Image
                src={repairImageKitUrl(p.image)}
                alt=''
                unoptimized={p.imageType === 'external'}
                width={px}
                height={px}
                className='h-full w-full object-cover'
              />
            )}
          </span>
        ))}
      </div>
      <span className='text-sm text-foreground-tertiary'>
        {remaining > 0 && (
          <span className='font-medium text-foreground-secondary'>
            +{remaining}
          </span>
        )}
        {remaining > 0 && minPrice && ' · '}
        {minPrice && (
          <>
            from <span className='font-medium text-foreground'>{minPrice}</span>
          </>
        )}
      </span>
    </div>
  )
}

/** The headliner's product grid: up to 7 individual products from the
 *  collection, each shown as its image on a clean white tile (no grey well, no
 *  circle) with the name + "$price · retailer" beneath, capped by a
 *  "View all {N}" tile that links to the full collection. */
function HeadlinerProducts({
  guide,
  href,
}: {
  guide: GiftGuide
  href: string
}) {
  const products = (guide.products || []).slice(0, 7)
  const count = guideCount(guide)
  if (!products.length) return null
  return (
    <div className='grid w-full grid-cols-4 gap-x-4 gap-y-5'>
      {products.map((p, i) => {
        const price = fmtPrice(productPrice(p))
        return (
          <Link
            key={p.id || p.image || `headliner-product-${i}`}
            href={href}
            className='group flex flex-col gap-2'
          >
            <div className='relative flex aspect-square items-center justify-center overflow-hidden rounded-2xl bg-white ring-1 ring-border'>
              {p.image && (
                <Image
                  src={repairImageKitUrl(p.image)}
                  alt=''
                  unoptimized={p.imageType === 'external'}
                  fill
                  sizes='(min-width: 768px) 130px, 25vw'
                  className='object-contain p-2.5 transition-transform duration-500 ease-out group-hover:scale-[1.05]'
                />
              )}
            </div>
            <div className='min-w-0'>
              {p.title && (
                <p className='line-clamp-1 text-xs font-semibold leading-tight text-foreground transition-colors group-hover:text-info-700'>
                  {p.title}
                </p>
              )}
              {(price || p.source) && (
                <p className='line-clamp-1 text-xs text-foreground-tertiary'>
                  {price && (
                    <span className='font-medium text-foreground-secondary'>
                      {price}
                    </span>
                  )}
                  {price && p.source && ' · '}
                  {p.source}
                </p>
              )}
            </div>
          </Link>
        )
      })}

      <Link href={href} className='group flex flex-col gap-2'>
        <div className='flex aspect-square flex-col items-center justify-center gap-1.5 rounded-2xl border border-dashed border-border text-center transition-colors hover:border-info-500/50 hover:bg-surface'>
          {count > 0 && (
            <span className='font-display text-xl font-bold leading-none text-foreground'>
              {count}
            </span>
          )}
          <span className='inline-flex items-center gap-1 text-xs font-medium text-info-700'>
            View all
            <span
              aria-hidden='true'
              className='transition-transform duration-200 group-hover:translate-x-0.5'
            >
              →
            </span>
          </span>
        </div>
      </Link>
    </div>
  )
}

const ImageWell = ({
  guide,
  sizes,
  className,
}: {
  guide: GiftGuide
  sizes: string
  className?: string
}) => (
  <div
    className={cn(
      'relative w-full overflow-hidden rounded-3xl bg-surface-muted dark:bg-surface-soft ring-1 ring-border',
      className
    )}
  >
    {guide.image && (
      <Image
        src={repairImageKitUrl(guide.image)}
        alt={guide.name || ''}
        unoptimized={guide.imageType === 'external'}
        fill
        sizes={sizes}
        className='object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]'
      />
    )}
  </div>
)

const Kicker = ({ children }: { children: React.ReactNode }) => (
  <span className='text-xs font-semibold uppercase tracking-[0.18em] text-info-700'>
    {children}
  </span>
)

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

/** The big two-column hero for the lead guide. */
export function ObsessionsHeadliner({ guide }: { guide: GiftGuide }) {
  const href = guideHref(guide, 'gift-guide-headliner')
  const minPrice = fmtPrice(guideMinPrice(guide))
  const count = guideCount(guide)
  return (
    <section className='grid grid-cols-1 items-stretch gap-8 md:grid-cols-2 md:gap-12 lg:gap-16'>
      <div className='order-2 flex flex-col items-start gap-5 md:order-1'>
        <Kicker>The headliner</Kicker>
        <Link href={href} className='group'>
          <h2 className='text-balance font-display text-4xl font-bold leading-[1.05] tracking-tight text-foreground transition-colors group-hover:text-info-700 md:text-5xl lg:text-6xl'>
            {guide.name}
          </h2>
        </Link>
        {guide.description && (
          <p className='max-w-xl text-pretty text-base leading-[1.45] text-foreground-secondary md:text-lg'>
            {guide.description}
          </p>
        )}
        {(count > 0 || minPrice) && (
          <p className='text-sm text-foreground-tertiary'>
            {count > 0 && (
              <>
                <span className='font-medium text-foreground-secondary'>
                  {count}
                </span>{' '}
                finds
              </>
            )}
            {count > 0 && minPrice && ' · '}
            {minPrice && (
              <>
                from{' '}
                <span className='font-medium text-foreground'>{minPrice}</span>
              </>
            )}
          </p>
        )}
        <div className='w-full pt-1'>
          <HeadlinerProducts guide={guide} href={href} />
        </div>
      </div>
      <Link href={href} className='group order-1 block md:order-2 md:h-full'>
        <ImageWell
          guide={guide}
          className='aspect-[4/3] md:aspect-auto md:h-full'
          sizes='(min-width: 768px) 48vw, 100vw'
        />
      </Link>
    </section>
  )
}

/** A featured guide card: image well + kicker + title + swatch stack. */
export function ObsessionsFeatureCard({
  guide,
  kicker = 'Featured',
}: {
  guide: GiftGuide
  kicker?: string
}) {
  const href = guideHref(guide, 'gift-guide-featured')
  return (
    <Link href={href} className='group flex flex-col gap-4'>
      <ImageWell
        guide={guide}
        className='aspect-[4/3]'
        sizes='(min-width: 768px) 31vw, 100vw'
      />
      <div className='flex flex-col gap-2.5'>
        <Kicker>{kicker}</Kicker>
        <h3 className='font-display text-xl font-bold leading-snug tracking-tight text-foreground transition-colors group-hover:text-info-700 md:text-2xl'>
          {guide.name}
        </h3>
        {guide.description && (
          <p className='line-clamp-2 text-sm leading-[1.45] text-foreground-secondary'>
            {guide.description}
          </p>
        )}
        <ProductSwatches guide={guide} />
      </div>
    </Link>
  )
}

/** A collection spotlight row: hero image on the left, description + a row of
 *  individual product cards (image · name · "$price · retailer") on the right,
 *  capped by a "+N · View collection" card. Everything links to the collection. */
export function ObsessionsCollectionSpotlight({ guide }: { guide: GiftGuide }) {
  const href = guideHref(guide, 'gift-guide-spotlight')
  const products = (guide.products || []).slice(0, 3)
  const remaining = Math.max(0, guideCount(guide) - products.length)
  return (
    <section className='grid grid-cols-1 items-stretch gap-6 md:grid-cols-[1fr_2fr] md:gap-8'>
      <Link
        href={href}
        className='group relative block min-h-[220px] overflow-hidden rounded-3xl bg-surface-muted dark:bg-surface-soft ring-1 ring-border md:h-full'
      >
        {guide.image && (
          <Image
            src={repairImageKitUrl(guide.image)}
            alt={guide.name || ''}
            unoptimized={guide.imageType === 'external'}
            fill
            sizes='(min-width: 768px) 33vw, 100vw'
            className='object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]'
          />
        )}
      </Link>

      <div className='flex flex-col gap-6'>
        <div className='flex flex-col gap-2'>
          <Link href={href} className='group w-fit'>
            <h3 className='font-display text-lg font-bold tracking-tight text-foreground transition-colors group-hover:text-info-700'>
              {guide.name}
            </h3>
          </Link>
          {guide.description && (
            <p className='max-w-3xl text-pretty text-base leading-[1.45] text-foreground-secondary'>
              {guide.description}
            </p>
          )}
        </div>

        <div className='grid grid-cols-2 gap-4 sm:grid-cols-4'>
          {products.map((p, i) => {
            const price = fmtPrice(productPrice(p))
            return (
              <Link
                key={p.id || p.image || `sp-${i}`}
                href={href}
                className='group flex flex-col gap-2.5'
              >
                <div className='flex aspect-square items-center justify-center overflow-hidden rounded-2xl bg-surface-muted dark:bg-surface-soft'>
                  <span className='relative size-[88%] overflow-hidden rounded-full bg-white ring-1 ring-border'>
                    {p.image && (
                      <Image
                        src={repairImageKitUrl(p.image)}
                        alt=''
                        unoptimized={p.imageType === 'external'}
                        fill
                        sizes='160px'
                        className='object-contain p-2 transition-transform duration-500 ease-out group-hover:scale-[1.04]'
                      />
                    )}
                  </span>
                </div>
                <div className='min-w-0'>
                  {p.title && (
                    <p className='line-clamp-1 text-sm font-semibold text-foreground'>
                      {p.title}
                    </p>
                  )}
                  {(price || p.source) && (
                    <p className='line-clamp-1 text-sm text-foreground-tertiary'>
                      {price && (
                        <span className='font-medium text-foreground-secondary'>
                          {price}
                        </span>
                      )}
                      {price && p.source && ' · '}
                      {p.source}
                    </p>
                  )}
                </div>
              </Link>
            )
          })}

          <Link
            href={href}
            className='group flex h-full min-h-[120px] flex-col items-center justify-center gap-1 rounded-2xl border border-dashed border-border p-4 text-center transition-colors hover:border-info-500/50 hover:bg-surface'
          >
            {remaining > 0 && (
              <span className='font-display text-2xl font-bold text-foreground'>
                +{remaining}
              </span>
            )}
            <span className='text-sm font-medium text-info-700'>
              View collection →
            </span>
          </Link>
        </div>
      </div>
    </section>
  )
}

/** A compact catalogue row: small hero thumbnail + title + swatch stack + arrow. */
export function ObsessionsCatalogueRow({ guide }: { guide: GiftGuide }) {
  const href = guideHref(guide, 'gift-guide-catalogue')
  return (
    <Link
      href={href}
      className='group flex items-center gap-4 border-t border-border py-5'
    >
      <div className='relative size-16 shrink-0 overflow-hidden rounded-xl bg-surface-muted dark:bg-surface-soft ring-1 ring-border'>
        {guide.image && (
          <Image
            src={repairImageKitUrl(guide.image)}
            alt=''
            unoptimized={guide.imageType === 'external'}
            fill
            sizes='64px'
            className='object-cover transition-transform duration-500 ease-out group-hover:scale-[1.05]'
          />
        )}
      </div>
      <div className='flex min-w-0 flex-1 flex-col gap-2'>
        <h3 className='line-clamp-2 font-display text-base font-bold leading-snug tracking-tight text-foreground transition-colors group-hover:text-info-700'>
          {guide.name}
        </h3>
        <ProductSwatches guide={guide} size='sm' />
      </div>
      <span
        aria-hidden='true'
        className='shrink-0 text-lg leading-none text-foreground-tertiary transition-all duration-200 group-hover:translate-x-1 group-hover:text-info-700'
      >
        →
      </span>
    </Link>
  )
}
